"use client";

import { useTranslation } from "@/components/language/hooks/useTranslation";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";

export default function SustainabilityPage() {
  const containerRef = useRef(null);
  const { scrollYProgress } = useScroll({
    target: containerRef,
    offset: ["start start", "end end"],
  });
  const { t, tm } = useTranslation();

  const opacity = useTransform(scrollYProgress, [0, 0.2], [1, 0]);
  const scale = useTransform(scrollYProgress, [0, 0.2], [1, 0.95]);

  const pillars: any[] = tm("sustain.pillars");


const impacts = [
  { value: "Ongoing", label: "Operational Improvements", color: "text-brand-ok/90" },
  { value: "Active", label: "Sustainability Considerations", color: "text-brand-teal/90" },
  { value: "Focused", label: "Customer Education Efforts", color: "text-brand-purple/80" },
  { value: "Committed", label: "Responsible Business Practices", color: "text-brand-teal" },
];

  return (
    <div ref={containerRef} className="overflow-hidden text-white bg-slate-950">
      <div className="fixed inset-0 z-0">
        <div className="absolute top-0 rounded-full left-1/4 w-96 h-96 bg-emerald-500/20 blur-3xl animate-pulse" />
        <div className="absolute delay-1000 rounded-full bottom-1/4 right-1/4 w-96 h-96 bg-blue-500/20 blur-3xl animate-pulse" />
        <div className="absolute rounded-full top-1/2 left-1/2 w-96 h-96 bg-violet-500/20 blur-3xl animate-pulse delay-2000" />
      </div>
      <section className="relative z-10 flex items-center justify-center min-h-screen px-6">
        <motion.div style={{ opacity, scale }} className="max-w-5xl mx-auto space-y-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            className="inline-block px-4 py-2 mb-4 border rounded-full bg-emerald-500/10 border-emerald-500/20"
          >
            <span className="text-sm font-medium text-emerald-400">{t("sustain.badge")} </span>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="text-3xl font-bold leading-tight md:text-8xl"
          >
            <span className="text-transparent bg-gradient-to-r from-emerald-400 via-blue-400 to-violet-400 bg-clip-text">
              {t("sustain.hero.title1")}
            </span>
            <br />
            <span className="text-slate-100">{t("sustain.hero.title2")} </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-3xl mx-auto text-xl leading-relaxed md:text-2xl text-slate-400"
          >
            {t("sustain.hero.description")}
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="pt-8"
          >
            <a
              href="#pillars"
              className="inline-flex items-center gap-2 transition-colors text-slate-400 hover:text-white group"
            >
              <span>{t("sustain.explore")} </span>
              <svg
                className="w-5 h-5 transition-transform transform group-hover:translate-y-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            </a>
          </motion.div>
        </motion.div>
      </section>
      <section id="pillars" className="relative z-10 px-6 py-32">
        <div className="mx-auto max-w-7xl 3xl:max-w-[1432px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-20 text-center"
          >
            <h2 className="mb-6 text-3xl font-bold md:text-6xl">
              {t("sustain.pillarsTitle1")}{" "}
              <span className="text-transparent bg-gradient-to-r from-emerald-400 to-blue-400 bg-clip-text">
                {t("sustain.pillarsTitle2")}{" "}
              </span>
            </h2>
            <p className="max-w-2xl mx-auto text-xl text-slate-400">{t("sustain.pillarsDesc")} </p>
          </motion.div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            {pillars.map((pillar, index) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.6, delay: index * 0.1 }}
                whileHover={{ y: -8, transition: { duration: 0.3 } }}
                className="relative group"
              >
                <div className="relative h-full p-8 overflow-hidden border bg-slate-900/50 backdrop-blur-sm border-slate-800 rounded-3xl">
                  <div
                    className={`absolute inset-0 bg-gradient-to-br from-${pillar.color}-500/0 to-${pillar.color}-500/0 group-hover:from-${pillar.color}-500/10 group-hover:to-transparent transition-all duration-500 rounded-3xl`}
                  />

                  <div className="relative z-10 space-y-6">
                    <div className="text-6xl">{pillar.icon}</div>
                    <h3 className="text-2xl font-bold text-white">{pillar.title}</h3>
                    <p className="text-md leading-relaxed text-slate-400">{pillar.description}</p>
                    <div className="pt-4 border-t border-slate-800">
                      <div className="flex items-baseline gap-2">
                        <span className={`text-4xl font-bold text-${pillar.color}-400`}>
                          {pillar.stats.value}
                        </span>
                        <span className="hidden md:block text-sm text-slate-500">{pillar.stats.label}</span>
                      </div>
                    </div>
                  </div>
                  <div
                    className={`absolute top-0 right-0 w-32 h-32 bg-gradient-to-bl from-${pillar.color}-500/20 to-transparent rounded-bl-full`}
                  />
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="relative z-10 px-6 py-32 bg-slate-900/30">
        <div className="mx-auto max-w-7xl 3xl:max-w-[1432px]">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="mb-20 text-center"
          >
            <h2 className="text-3xl md:text-6xl font-bold mb-6 text-brand-text">
              Impact Breakdown
                Breakdown

            </h2>
            <p className="max-w-2xl mx-auto text-md md:text-xl text-slate-400">{t("sustain.impactDesc")} </p>
          </motion.div>
          <div className="grid sm:grid-cols-2 gap-6 lg:grid-cols-4">
            {impacts.map((impact, index) => (
              <motion.div
                key={impact.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true, margin: "-100px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="relative group"
              >
                <div className="p-8 text-center transition-all duration-300 border bg-slate-900/50 backdrop-blur-sm border-slate-800 global-border-radius hover:border-slate-700">
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.5, delay: index * 0.1 + 0.2 }}
                  >
                    <div className={`text-5xl font-bold mb-3 ${impact.color}`}>{impact.value}</div>
                    <div className="text-sm font-medium text-slate-400">{impact.label}</div>
                  </motion.div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
      <section className="relative z-10 px-6 py-32">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.8 }}
            className="space-y-8 text-center"
          >
            <h2 className="text-5xl md:text-6xl font-bold leading-tight text-brand-text">
              Beyond{" "}
              {/* <span className="bg-gradient-to-r from-emerald-400 via-blue-400 to-violet-400 bg-clip-text text-transparent"> */}
                Carbon Neutral
              {/* </span> */}
            </h2>
            <p className="text-xl leading-relaxed text-slate-400">{t("sustain.beyondDesc")}</p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.3 }}
              className="pt-8"
            >
              <div className="inline-flex flex-col gap-4 sm:flex-row">
                <a
                  href="/register"
                  className="group relative px-8 py-4 bg-brand-grey rounded-full font-semibold text-brand-card overflow-hidden hover:shadow-2xl hover:shadow-emerald-500/50 transition-all duration-300"
                >
                  <span className="relative z-10">{t("sustain.partner")} </span>
                  <div className="absolute inset-0 transition-opacity duration-300 opacity-0 bg-gradient-to-r from-emerald-600 to-blue-600 group-hover:opacity-100" />
                </a>
                <a
                  href="#"
                  className="px-8 py-4 font-semibold text-white transition-all duration-300 border rounded-full bg-slate-900/50 backdrop-blur-sm border-slate-700 hover:bg-slate-800/50"
                >
                  {t("sustain.report")}{" "}
                </a>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </section>
      <div className="relative z-0">
        <svg
          className="w-full h-32 md:h-48"
          viewBox="0 0 1440 320"
          preserveAspectRatio="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <defs>
            <linearGradient id="waveGradient" x1="0%" y1="0%" x2="100%" y2="0%">
              <stop offset="0%" stopColor="#10b981" stopOpacity="0.2" />
              <stop offset="50%" stopColor="#3b82f6" stopOpacity="0.2" />
              <stop offset="100%" stopColor="#8b5cf6" stopOpacity="0.2" />
            </linearGradient>
          </defs>
          <path
            fill="url(#waveGradient)"
            d="M0,160L48,144C96,128,192,96,288,90.7C384,85,480,107,576,128C672,149,768,171,864,165.3C960,160,1056,128,1152,122.7C1248,117,1344,139,1392,149.3L1440,160L1440,320L1392,320C1344,320,1248,320,1152,320C1056,320,960,320,864,320C768,320,672,320,576,320C480,320,384,320,288,320C192,320,96,320,48,320L0,320Z"
          />
        </svg>
      </div>
    </div>
  );
}
